import { TZDate } from '@date-fns/tz'
import { addDays, subDays } from 'date-fns'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	CALENDAR_END_HOUR,
	CALENDAR_START_HOUR,
	dateToMinutesFromMidnight,
	durationToPx,
	findSlotStartInRegion,
	formatDateInTimezone,
	formatHourLabel,
	formatTimeLabel,
	getRegionsForDate,
	HOUR_HEIGHT_PX,
	minutesToPx,
	pxToMinutes,
} from './calendar-math'
import type { PlaceableRegion, SelectedSlot } from './types'

interface Props {
	regions: PlaceableRegion[]
	durationMinutes: number
	candidateTimezone: string
	selectedSlot: SelectedSlot | null
	onSlotSelect: (slot: SelectedSlot) => void
	initialAnchor: Date
	columnCount?: number
}

const CALENDAR_HEIGHT = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * HOUR_HEIGHT_PX

function minutesOnDateToUtc(minutesFromMidnight: number, localDateStr: string, timezone: string): Date {
	const hours = Math.floor(minutesFromMidnight / 60)
	const minutes = minutesFromMidnight % 60
	const tzDate = new TZDate(
		parseInt(localDateStr.slice(0, 4)),
		parseInt(localDateStr.slice(5, 7)) - 1,
		parseInt(localDateStr.slice(8, 10)),
		hours,
		minutes,
		0,
		timezone,
	)
	return new Date(tzDate.getTime())
}

export function CalendarDayColumns({
	regions,
	durationMinutes,
	candidateTimezone,
	selectedSlot,
	onSlotSelect,
	initialAnchor,
	columnCount = 2,
}: Props) {
	const { t, i18n } = useTranslation('booking')
	const [anchorDate, setAnchorDate] = useState(initialAnchor)
	const visibleDates = Array.from({ length: columnCount }, (_, i) => addDays(anchorDate, i))

	// Two-step touch: first tap sets preview, panel asks to confirm
	const [preview, setPreview] = useState<{ dayIndex: number; startMinutes: number } | null>(null)
	const columnRefs = useRef<(HTMLDivElement | null)[]>([])

	function handleTap(dayIndex: number, e: React.MouseEvent<HTMLDivElement>) {
		const col = columnRefs.current[dayIndex]
		if (!col) { return }
		const rect = col.getBoundingClientRect()
		const yPx = e.clientY - rect.top
		const cursorMinutes = pxToMinutes(yPx)

		if (cursorMinutes < CALENDAR_START_HOUR * 60 || cursorMinutes > CALENDAR_END_HOUR * 60) {
			setPreview(null)
			return
		}

		const dayRegions = getRegionsForDate(visibleDates[dayIndex], regions, candidateTimezone)
		const slotStart = findSlotStartInRegion(cursorMinutes, durationMinutes, dayRegions, candidateTimezone)
		if (slotStart !== null) {
			// If already previewing this same slot — treat second tap as deselect
			if (preview?.dayIndex === dayIndex && preview.startMinutes === slotStart) {
				setPreview(null)
			} else {
				setPreview({ dayIndex, startMinutes: slotStart })
			}
		} else {
			setPreview(null)
		}
	}

	function confirmPreview() {
		if (!preview) { return }
		const date = visibleDates[preview.dayIndex]
		const localDateStr = formatDateInTimezone(date, candidateTimezone)
		const startsAt = minutesOnDateToUtc(preview.startMinutes, localDateStr, candidateTimezone)
		const endsAt = new Date(startsAt.getTime() + durationMinutes * 60_000)
		onSlotSelect({ startsAt, endsAt })
		setPreview(null)
	}

	const todayStr = formatDateInTimezone(new Date(), candidateTimezone)

	return (
		<div className="flex flex-col gap-3">
			{/* Day navigation */}
			<div className="flex items-center justify-between">
				<button
					type="button"
					onClick={() => {
						setAnchorDate((d) => subDays(d, columnCount))
						setPreview(null)
					}}
					className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
				>
					‹
				</button>
				<span className="text-sm font-medium text-gray-600">
					{visibleDates[0].toLocaleDateString(i18n.language, {
						month: 'short',
						day: 'numeric',
						timeZone: candidateTimezone,
					})}
					{columnCount > 1 && (
						<>
							{' – '}
							{visibleDates[columnCount - 1].toLocaleDateString(i18n.language, {
								month: 'short',
								day: 'numeric',
								timeZone: candidateTimezone,
							})}
						</>
					)}
				</span>
				<button
					type="button"
					onClick={() => {
						setAnchorDate((d) => addDays(d, columnCount))
						setPreview(null)
					}}
					className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
				>
					›
				</button>
			</div>

			{/* Calendar columns */}
			<div className="flex overflow-hidden">
				{/* Hour labels */}
				<div className="mr-2 flex shrink-0 flex-col" style={{ width: 28, paddingTop: 32 }}>
					<div className="relative" style={{ height: CALENDAR_HEIGHT }}>
						{Array.from({ length: CALENDAR_END_HOUR - CALENDAR_START_HOUR + 1 }, (_, i) => {
							const hour = CALENDAR_START_HOUR + i
							if (hour % 2 === 0) { return null }
							return (
								<div
									key={hour}
									className="absolute right-0 text-[10px] text-gray-300"
									style={{ top: i * HOUR_HEIGHT_PX - 6 }}
								>
									{formatHourLabel(hour)}
								</div>
							)
						})}
					</div>
				</div>

				{/* Day columns */}
				<div className="flex flex-1 gap-px">
					{visibleDates.map((date, dayIndex) => {
						const localDateStr = formatDateInTimezone(date, candidateTimezone)
						const isToday = localDateStr === todayStr
						const dayRegions = getRegionsForDate(date, regions, candidateTimezone)
						const isPreviewDay = preview?.dayIndex === dayIndex
						const selectedOnThisDay = selectedSlot
							? formatDateInTimezone(selectedSlot.startsAt, candidateTimezone) === localDateStr
							: false

						return (
							<div key={dayIndex} className="flex min-w-0 flex-1 flex-col">
								{/* Day header */}
								<div className="mb-1 text-center">
									<div
										className={`text-[10px] uppercase ${
											isToday ? 'font-bold text-gray-900' : 'text-gray-400'
										}`}
									>
										{date.toLocaleDateString(i18n.language, {
											weekday: 'short',
											timeZone: candidateTimezone,
										})}
									</div>
									<div
										className={`text-xs font-semibold ${
											isToday ? 'text-blue-600' : 'text-gray-600'
										}`}
									>
										{date.toLocaleDateString(i18n.language, {
											day: 'numeric',
											timeZone: candidateTimezone,
										})}
									</div>
								</div>

								{/* Column grid */}
								<div
									ref={(el) => {
										columnRefs.current[dayIndex] = el
									}}
									className="relative cursor-pointer bg-gray-50"
									style={{ height: CALENDAR_HEIGHT }}
									onClick={(e) => handleTap(dayIndex, e)}
								>
									{/* Hour gridlines */}
									{Array.from({ length: CALENDAR_END_HOUR - CALENDAR_START_HOUR + 1 }, (_, i) => (
										<div
											key={i}
											className="absolute inset-x-0 border-t border-gray-100"
											style={{ top: i * HOUR_HEIGHT_PX }}
										/>
									))}

									{/* Available regions */}
									{dayRegions.map((region, regionIndex) => {
										const regionStartMinutes = dateToMinutesFromMidnight(
											region.placeableFrom,
											candidateTimezone,
										)
										const regionEndMinutes = dateToMinutesFromMidnight(
											region.placeableTo,
											candidateTimezone,
										)
										const top = minutesToPx(regionStartMinutes)
										const height = minutesToPx(regionEndMinutes) - top
										return (
											<div
												key={regionIndex}
												className="absolute inset-x-0 bg-blue-50"
												style={{ top, height }}
											/>
										)
									})}

									{/* Preview box (first tap — stays visible until confirmed or cleared) */}
									{Boolean(isPreviewDay) && preview != null && (
										<div
											className="pointer-events-none absolute inset-x-0.5 rounded bg-blue-400/40 ring-2 ring-blue-400"
											style={{
												top: minutesToPx(preview.startMinutes),
												height: durationToPx(durationMinutes),
											}}
										>
											<span className="block px-1 pt-0.5 text-[10px] font-medium text-blue-700">
												{formatTimeLabel(preview.startMinutes, candidateTimezone)}
											</span>
										</div>
									)}

									{/* Confirmed selected slot */}
									{Boolean(selectedOnThisDay) && selectedSlot != null && (() => {
										const startMinutes = dateToMinutesFromMidnight(
											selectedSlot.startsAt,
											candidateTimezone,
										)
										return (
											<div
												className="pointer-events-none absolute inset-x-0.5 rounded bg-blue-600 ring-1 ring-blue-700"
												style={{
													top: minutesToPx(startMinutes),
													height: durationToPx(durationMinutes),
												}}
											>
												<span className="block px-1 pt-0.5 text-[10px] font-medium text-white">
													{formatTimeLabel(startMinutes, candidateTimezone)}
												</span>
											</div>
										)
									})()}
								</div>
							</div>
						)
					})}
				</div>
			</div>

			{/* Two-step confirm panel — slides up after first tap */}
			{preview != null && (
				<div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
					<p className="text-sm font-medium text-gray-800">
						{t('confirm.slot_on', {
							time: formatTimeLabel(preview.startMinutes, candidateTimezone),
							date: visibleDates[preview.dayIndex].toLocaleDateString(i18n.language, {
								weekday: 'short',
								month: 'short',
								day: 'numeric',
								timeZone: candidateTimezone,
							}),
						})}
					</p>
					<div className="mt-2 flex gap-2">
						<button
							type="button"
							onClick={confirmPreview}
							className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white"
						>
							{t('confirm.book_this_time')}
						</button>
						<button
							type="button"
							onClick={() => setPreview(null)}
							className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600"
						>
							{t('confirm.choose_different')}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
